import React from 'react';

import { ObjectFieldTemplateProps, RJSFSchema } from '@rjsf/utils';

import { Box } from '@mui/material';

/**
 * GridObjectFieldTemplate - A custom RJSF ObjectFieldTemplate that renders fields
 * in a responsive CSS Grid layout.
 *
 * Features:
 * - 6-column grid on xl screens (1536px+), single column below
 * - Default field width: 2 columns (33%) on large screens
 * - Configurable per-field widths via ui:colSpan in uiSchema
 * - Opt-in via ui:options.gridLayout in uiSchema
 *
 * Usage:
 * ```tsx
 * const uiSchema = {
 *   'ui:options': { gridLayout: true },
 *   'ui:order': ['field1', 'field2', ...],
 *   fieldName: { 'ui:colSpan': 6 },  // Full width
 *   otherField: { 'ui:colSpan': 3 }, // Half width
 *   // Fields without ui:colSpan default to 2 (33%)
 * };
 *
 * <Form
 *   templates={{ ObjectFieldTemplate: GridObjectFieldTemplate }}
 *   uiSchema={uiSchema}
 *   // ...
 * />
 * ```
 *
 * Column span values:
 * - 2 = 33% width (default)
 * - 3 = 50% width
 * - 4 = 66% width
 * - 6 = 100% width (full row)
 */

const DEFAULT_COL_SPAN = 2;

interface GridObjectFieldTemplateProps extends ObjectFieldTemplateProps<unknown, RJSFSchema> {
  uiSchema?: {
    'ui:options'?: {
      gridLayout?: boolean;
    };
    [key: string]: unknown;
  };
}

export default function GridObjectFieldTemplate({
  properties,
  uiSchema,
  title,
  description,
  fieldPathId,
}: GridObjectFieldTemplateProps) {
  const gridLayoutEnabled = uiSchema?.['ui:options']?.gridLayout;

  // If grid layout is not enabled, render with default stacking behavior
  if (!gridLayoutEnabled) {
    return (
      <Box component="fieldset" id={fieldPathId.$id} sx={{ border: 'none', p: 0, m: 0 }}>
        {title && <legend>{title}</legend>}
        {description}
        {properties.map((element) => (
          <Box key={element.name} sx={{ mb: 2 }}>
            {element.content}
          </Box>
        ))}
      </Box>
    );
  }

  // Grid layout enabled - render in responsive CSS Grid
  return (
    <Box component="fieldset" id={fieldPathId.$id} sx={{ border: 'none', p: 0, m: 0 }}>
      {title && <legend>{title}</legend>}
      {description}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl: 'repeat(6, 1fr)',
          },
          gap: 2,
        }}
      >
        {properties.map((element) => {
          const fieldUiSchema = uiSchema?.[element.name] as Record<string, unknown> | undefined;
          const colSpan = (fieldUiSchema?.['ui:colSpan'] as number) ?? DEFAULT_COL_SPAN;

          // Hide hidden fields
          if (element.hidden) {
            return (
              <Box key={element.name} sx={{ display: 'none' }}>
                {element.content}
              </Box>
            );
          }

          return (
            <Box
              key={element.name}
              sx={{
                gridColumn: {
                  xs: 'span 1',
                  xl: `span ${colSpan}`,
                },
              }}
            >
              {element.content}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
